import Carousel from '../legacy/components/carousel.js';
import { createDebugger } from '../utils/debug.js';
import '../utils/resize-event.js';

const debug = createDebugger('variant-features-carousel', false);

// Configuration constants
const CONFIG = {
    FEATURE_THRESHOLD: 4,
    BREAKPOINT_LG: 1024,
    MOBILE_SPACING: 16,
    DESKTOP_SPACING: 24,
    MOBILE_SLIDES_PER_VIEW: 2,
    DESKTOP_SLIDES_PER_VIEW: 4
};

/**
 * Variant Features Carousel Component
 * Initializes carousel on mobile only, grid on desktop
 * Follows the multi-column pattern for breakpoint-based initialization
 */
class VariantFeaturesCarousel {
    constructor(container) {
        this.container = container;
        this.carousel = null;
        this.breakPointHandler = null;
        this.init();
    }

    init() {
        debug.log('VariantFeaturesCarousel init', { container: this.container });

        const { sliderOnMobile, desktopColumnsPerView, mobileColumnsPerView } = this.container.dataset;

        debug.log('Dataset values:', {
            sliderOnMobile,
            desktopColumnsPerView,
            mobileColumnsPerView,
            allDataset: this.container.dataset
        });

        const enableMobileSlider = sliderOnMobile !== 'false';
        const enableDesktopSlider = !!desktopColumnsPerView;

        debug.log('Slider configuration:', {
            enableMobileSlider,
            enableDesktopSlider
        });

        // If desktop slider is enabled, always init carousel (works on all breakpoints)
        if (enableDesktopSlider) {
            debug.log('Desktop slider enabled, initializing for all breakpoints');
            this._initCarousel();
            return;
        }

        // Mobile-only slider logic (original behavior)
        if (!enableMobileSlider) {
            debug.log('Mobile slider disabled, skipping initialization');
            return;
        }

        // Initialize carousel if currently on mobile
        if (this._isMobile()) {
            this._initCarousel();
        }

        // Watch for breakpoint changes
        this.breakPointHandler = this._atBreakpointChange(1024, () => {
            debug.log('Breakpoint change detected', { isMobile: this._isMobile() });

            if (this._isMobile()) {
                // Switched to mobile - init carousel
                if (!this.carousel) {
                    this._initCarousel();
                }
            } else {
                // Switched to desktop - destroy carousel
                if (this.carousel) {
                    debug.log('Destroying carousel for desktop view');
                    this.carousel.destroy();
                    this.carousel = null;
                }
            }
        });
    }

    _isMobile() {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        return viewportWidth < CONFIG.BREAKPOINT_LG;
    }

    _initCarousel() {
        if (this.carousel) {
            debug.log('Carousel already initialized, skipping');
            return;
        }

        try {
            const {
                mobileColumnsPerView = String(CONFIG.MOBILE_SLIDES_PER_VIEW),
                desktopColumnsPerView,
                enablePagination
            } = this.container.dataset;

            debug.log('Initializing carousel with container:', { container: this.container });

            const carouselOptions = {
                slidesPerView: parseInt(mobileColumnsPerView, 10),
                spaceBetween: CONFIG.MOBILE_SPACING,
                loop: false
            };

            // Add responsive breakpoints if desktop slider is enabled
            if (desktopColumnsPerView) {
                carouselOptions.breakpoints = {
                    [CONFIG.BREAKPOINT_LG]: {
                        slidesPerView: parseInt(desktopColumnsPerView, 10),
                        spaceBetween: CONFIG.DESKTOP_SPACING
                    }
                };
                debug.log('Desktop slider enabled with responsive breakpoints');
            }

            // Add pagination if enabled
            if (enablePagination === 'true') {
                const paginationEl = this.container.querySelector('.swiper-pagination');
                if (paginationEl) {
                    carouselOptions.pagination = {
                        el: paginationEl,
                        clickable: true
                    };
                    debug.log('Pagination enabled');
                }
            }

            debug.log('Initializing carousel with options', carouselOptions);

            this.carousel = Carousel(this.container, carouselOptions);

            if (!this.carousel) {
                throw new Error('Carousel initialization returned null');
            }

            debug.log('Carousel initialized', { carousel: this.carousel });
        } catch (error) {
            console.error('[variant-features-carousel] Failed to initialize carousel:', error);
            // Add fallback class for CSS to handle non-carousel layout
            this.container.classList.add('carousel-init-failed');
        }
    }

    // Breakpoint change watcher using global resize event
    _atBreakpointChange(breakpoint, callback) {
        let wasUnderBreakpoint = this._isMobile();

        const handleResize = () => {
            const isUnderBreakpoint = this._isMobile();
            if (isUnderBreakpoint !== wasUnderBreakpoint) {
                wasUnderBreakpoint = isUnderBreakpoint;
                callback();
            }
        };

        document.addEventListener('theme:resize', handleResize);

        return {
            unload: () => {
                document.removeEventListener('theme:resize', handleResize);
            }
        };
    }

    destroy() {
        debug.log('VariantFeaturesCarousel destroy');

        if (this.breakPointHandler) {
            this.breakPointHandler.unload();
            this.breakPointHandler = null;
        }

        if (this.carousel) {
            this.carousel.destroy();
            this.carousel = null;
        }
    }
}

// Initialize function
function initializeCarousels() {
    const carouselContainers = document.querySelectorAll('[data-variant-features-carousel]');

    debug.log('Initializing carousels', {
        count: carouselContainers.length,
        readyState: document.readyState
    });

    carouselContainers.forEach((container) => {
        // Skip if already initialized
        if (container._variantFeaturesCarousel) {
            debug.log('Container already initialized, skipping', { container });
            return;
        }

        // Store instance on the element for cleanup
        container._variantFeaturesCarousel = new VariantFeaturesCarousel(container);
    });
}

// Global event listeners - intentionally persist for page lifetime
// These listeners are lightweight and necessary for the component to work across
// page interactions, theme editor changes, and variant selections

// Initialize on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCarousels);
} else {
    // DOM already loaded, initialize immediately
    initializeCarousels();
}

// Also listen for theme:loaded event (Stiletto pattern)
document.addEventListener('theme:loaded', () => {
    debug.log('theme:loaded event received, initializing carousels');
    initializeCarousels();
});

// Listen for reload-on-event:loaded (for variant changes)
document.addEventListener('reload-on-event:loaded', (event) => {
    debug.log('reload-on-event:loaded received', { detail: event.detail });
    // Re-initialize after DOM is painted
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            initializeCarousels();
        });
    });
});

// Theme Editor Events - reinitialize when section settings change
if (window.Shopify && window.Shopify.designMode) {
    debug.log('Theme editor detected, setting up editor event listeners');

    // When section is loaded/reloaded (settings changed)
    document.addEventListener('shopify:section:load', (event) => {
        debug.log('shopify:section:load received', { detail: event.detail });

        // Only reinitialize if it's the variant-features section
        const sectionContainer = event.target;
        const carouselContainer = sectionContainer.querySelector('[data-variant-features-carousel]');

        if (carouselContainer) {
            debug.log('Variant features section detected, reinitializing carousel');

            // Destroy existing instance if it exists
            if (carouselContainer._variantFeaturesCarousel) {
                debug.log('Destroying existing carousel instance');
                carouselContainer._variantFeaturesCarousel.destroy();
                delete carouselContainer._variantFeaturesCarousel;
            }

            // Reinitialize after DOM is painted
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    carouselContainer._variantFeaturesCarousel = new VariantFeaturesCarousel(
                        carouselContainer
                    );
                });
            });
        }
    });

    // When section is unloaded (removed)
    document.addEventListener('shopify:section:unload', (event) => {
        debug.log('shopify:section:unload received', { detail: event.detail });

        const sectionContainer = event.target;
        const carouselContainer = sectionContainer.querySelector('[data-variant-features-carousel]');

        if (carouselContainer && carouselContainer._variantFeaturesCarousel) {
            debug.log('Cleaning up variant features carousel');
            carouselContainer._variantFeaturesCarousel.destroy();
            delete carouselContainer._variantFeaturesCarousel;
        }
    });
}

// Cleanup on page unload - destroy all carousel instances
// This prevents memory leaks when navigating away from the page
window.addEventListener('beforeunload', () => {
    const carouselContainers = document.querySelectorAll('[data-variant-features-carousel]');
    carouselContainers.forEach((container) => {
        if (container._variantFeaturesCarousel) {
            container._variantFeaturesCarousel.destroy();
            delete container._variantFeaturesCarousel;
        }
    });
});

export default VariantFeaturesCarousel;
